import React from 'react'

import Localization from '~/client/src/shared/localization/LocalizationManager'
import {
  IBasemapAdmission,
  isBasemapGuardActive,
  requestBasemapAdmission,
} from '~/client/src/shared/utils/basemapRenderGuard'
import { toast } from '~/client/src/shared/utils/toast'

const OVERSIZED_TOAST_KEY = 'oversized-basemap'
const OVERSIZED_TOAST_TIMEOUT = 6000

/**
 * 1×1 transparent PNG served to the mapbox source while a basemap is pending
 * or blocked. Keeping the source/layer mounted with a placeholder (instead of
 * unmounting children) pins canvas layer order to mount order regardless of
 * admission timing, and guarantees the real URL never reaches mapbox before
 * its admission — including the re-render where a new url prop arrives with
 * the previous admission still in state.
 */
export const TRANSPARENT_PIXEL =
  'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAAC0lEQVR42mNgAAIAAAUAAen63NgAAAAASUVORK5CYII='

interface IProps {
  url: string
  children: (safeUrl: string) => React.ReactNode
}

interface IState {
  admittedUrl: string
}

export default class BasemapRenderGuard extends React.Component<
  IProps,
  IState
> {
  public static getDerivedStateFromProps(
    props: IProps,
    state: IState,
  ): Partial<IState> {
    return state.admittedUrl && state.admittedUrl !== props.url
      ? { admittedUrl: null }
      : null
  }

  public state: IState = { admittedUrl: null }
  private isUnmounted = false
  private admission: IBasemapAdmission = null

  public componentDidMount(): void {
    this.requestAdmission()
  }

  public componentDidUpdate(prevProps: IProps): void {
    if (prevProps.url === this.props.url) {
      return
    }
    this.admission?.release()
    this.admission = null
    this.requestAdmission()
  }

  public componentWillUnmount(): void {
    this.isUnmounted = true
    this.admission?.release()
  }

  public render(): React.ReactNode {
    const { url, children } = this.props
    if (!isBasemapGuardActive()) {
      return children(url)
    }
    return children(this.state.admittedUrl === url ? url : TRANSPARENT_PIXEL)
  }

  private requestAdmission(): void {
    if (!isBasemapGuardActive()) {
      return
    }
    const { url } = this.props
    const admission = requestBasemapAdmission(url)
    this.admission = admission
    admission.result.then(isAdmitted => {
      if (this.isUnmounted || this.admission !== admission) {
        return
      }
      if (isAdmitted) {
        this.setState({ admittedUrl: url })
      } else {
        toast.info(Localization.translator.sitemapsTooLargeForMobile, {
          timeout: OVERSIZED_TOAST_TIMEOUT,
          key: OVERSIZED_TOAST_KEY,
        })
      }
    })
  }
}
